/**
 * USYSWatch.tsx — Public landing page for Carmel FC 2013G Gold NL
 * USYS National Championships streaming hub.
 *
 * URL: /USYS
 * No login required.
 */
import React, { useEffect, useState } from 'react';
import { mediaUrl } from '../utils/mediaUrl';

// ── USYS brand colors ─────────────────────────────────────────────────────────
const USYS_DARK  = '#08122B';   // deep navy background (from banner)
const USYS_NAVY  = '#1A3464';   // shield / heading navy
const USYS_RED   = '#CE1126';   // stripes / accent red
const USYS_WHITE = '#ffffff';

// ── Logo / asset URLs ─────────────────────────────────────────────────────────
const USYS_SHIELD = '/api/videos/proxy/logos/usys_shield_logo.png';   // large USYS shield
const USYS_HEADER = '/api/videos/proxy/logos/usys_header_logo.png';   // horizontal "US Youth Soccer"
const CARMEL_LOGO = '/api/videos/proxy/logos/logo_a9aa2911-a077-417f-abd1-e1c8a714e3b7.jpeg';

// ── Group-play games ──────────────────────────────────────────────────────────
const GROUP_GAMES = [
  {
    id:           'bba82b1f-8c88-41ad-984b-3de6b3bd0bb3',
    label:        'Group Game 1',
    day:          'Thursday',
    date:         'July 16',
    time:         '2:45 PM CST / 3:45 PM EST',
    opponent:     'NCFC Youth Elite ECRL',
    opponentSub:  'North Carolina',
    opponentLogo: '/api/videos/proxy/photos/photo_99cf40ae-b83f-4b90-8e4d-81378585c79d.jpeg',
  },
  {
    id:           '9d8eda97-e4ed-4edb-86d5-61bacc751d0c',
    label:        'Group Game 2',
    day:          'Friday',
    date:         'July 17',
    time:         '2:45 PM CST / 3:45 PM EST',
    opponent:     'Tennessee SC',
    opponentSub:  'Tennessee',
    opponentLogo: '/api/videos/proxy/photos/photo_955ed403-ec46-4c3e-ae79-30a42f3ad2c9.png',
  },
  {
    id:           '66056f14-11ab-4257-a27b-6e115246f2b5',
    label:        'Group Game 3',
    day:          'Saturday',
    date:         'July 18',
    time:         '8:00 AM CST / 9:00 AM EST',
    opponent:     'Real Futbol Academy NAL',
    opponentSub:  'New Jersey',
    opponentLogo: '/api/videos/proxy/photos/photo_634a2ed3-dc1d-426c-a669-616f80eefe9f.jpeg',
  },
];

interface GameState {
  status: string;
  home_score?: number;
  away_score?: number;
  recording?: {
    id: string;
    file_path: string;
    compressed_path?: string | null;
  } | null;
}

// ── App Store badge ───────────────────────────────────────────────────────────
const AppStoreBadge: React.FC = () => (
  <a href="https://apps.apple.com/us/app/slow-rib-sports/id6760488944"
    target="_blank" rel="noopener noreferrer"
    style={{ display: 'inline-block', lineHeight: 0 }}>
    <svg width="140" height="42" viewBox="0 0 140 42" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="140" height="42" rx="7" fill="#000"/>
      <rect width="140" height="42" rx="7" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="0.8"/>
      <path d="M21.5 10.5c1.2-1.4 2-3.3 1.8-5.2-1.7.1-3.8 1.2-5 2.6-1.1 1.2-2 3.2-1.8 5 2 .2 3.9-.9 5-2.4z" fill="white"/>
      <path d="M23.3 13.3c-2.8-.2-5.1 1.6-6.4 1.6-1.3 0-3.3-1.5-5.4-1.5-2.8.1-5.4 1.7-6.8 4.2-2.9 5-.8 12.4 2 16.5 1.4 2 3 4.2 5.2 4.1 2-.1 2.8-1.3 5.2-1.3 2.4 0 3.1 1.3 5.2 1.3 2.2 0 3.7-2 5-4 1.6-2.3 2.2-4.5 2.3-4.6-.1 0-4.4-1.7-4.4-6.6 0-4.2 3.4-6.2 3.6-6.3-2-2.9-5-3.4-5.5-3.4z" fill="white"/>
      <text x="36" y="16" fill="white" fontFamily="-apple-system,Helvetica,Arial,sans-serif" fontSize="8" fontWeight="400" letterSpacing="0.2">Download on the</text>
      <text x="35" y="30" fill="white" fontFamily="-apple-system,Helvetica,Arial,sans-serif" fontSize="17" fontWeight="600" letterSpacing="-0.3">App Store</text>
    </svg>
  </a>
);

// ── Team logo with initials fallback ─────────────────────────────────────────
const TeamLogo: React.FC<{
  src: string | null;
  alt: string;
  size?: number;
  initials?: string;
  bgColor?: string;
  borderColor?: string;
}> = ({ src, alt, size = 58, initials = '?', bgColor = 'rgba(255,255,255,0.07)', borderColor = 'rgba(255,255,255,0.15)' }) => {
  const [errored, setErrored] = useState(false);
  return (
    <div style={{
      width: size, height: size, borderRadius: 12,
      background: bgColor, border: `1.5px solid ${borderColor}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0, overflow: 'hidden', boxShadow: '0 2px 10px rgba(0,0,0,0.4)',
    }}>
      {src && !errored
        ? <img src={mediaUrl(src)} alt={alt}
            style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 4 }}
            onError={() => setErrored(true)} />
        : <span style={{ fontSize: size * 0.26, fontWeight: 800, color: 'rgba(255,255,255,0.45)', letterSpacing: '-0.5px' }}>
            {initials}
          </span>
      }
    </div>
  );
};

// ── Stars decoration (USA flag feel) ─────────────────────────────────────────
const Stars: React.FC<{ count?: number }> = ({ count = 5 }) => (
  <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
    {Array.from({ length: count }).map((_, i) => (
      <svg key={i} width="10" height="10" viewBox="0 0 10 10" fill={USYS_RED}>
        <polygon points="5,0 6.2,3.6 10,3.6 7,5.9 8.1,9.5 5,7.3 1.9,9.5 3,5.9 0,3.6 3.8,3.6"/>
      </svg>
    ))}
  </div>
);

// ── Individual game card ──────────────────────────────────────────────────────
const GameCard: React.FC<{
  game: typeof GROUP_GAMES[number];
  index: number;
  phase?: string;
}> = ({ game, index, phase = 'group' }) => {
  const [state, setState] = useState<GameState | null>(null);
  const [loading, setLoading] = useState(true);
  const [showPlayer, setShowPlayer] = useState(false);

  useEffect(() => {
    const API_BASE = process.env.REACT_APP_API_URL ?? '/api';
    const fetch_ = () =>
      fetch(`${API_BASE}/games/${game.id}/public`)
        .then(r => r.ok ? r.json() : null)
        .then((data: any) => {
          if (!data) return;
          setState({ status: data.status, home_score: data.score?.home_score, away_score: data.score?.away_score, recording: data.recording ?? null });
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    fetch_();
    const t = setInterval(fetch_, 30_000);
    return () => clearInterval(t);
  }, [game.id]);

  const isLive      = state?.status === 'live';
  const isCompleted = state?.status === 'completed';
  const hasRecording = isCompleted && !!state?.recording?.file_path;

  const recordingUrl = hasRecording ? (() => {
    const HLS_BASE = process.env.REACT_APP_HLS_URL ?? '/hls';
    const fp = state!.recording!.compressed_path || state!.recording!.file_path;
    return (fp.startsWith('/api/') || fp.startsWith('http')) ? fp : `${HLS_BASE}${fp}`;
  })() : null;

  const scoreDisplay =
    isCompleted && state?.home_score !== undefined && state?.away_score !== undefined
      ? `${state.home_score}–${state.away_score}` : null;

  const carmelInitials = 'CFC';
  const oppInitials = game.opponent.split(' ').map(w => w[0]).join('').slice(0, 3).toUpperCase();

  return (
    <div style={{
      background:   'rgba(255,255,255,0.035)',
      border:       `1px solid ${isLive ? `${USYS_RED}88` : 'rgba(255,255,255,0.1)'}`,
      borderRadius: 16,
      overflow:     'hidden',
      boxShadow:    isLive ? `0 0 0 1px ${USYS_RED}44, 0 4px 24px ${USYS_RED}18` : '0 2px 14px rgba(0,0,0,0.3)',
    }}>

      {/* Card header strip — USYS navy with red left border */}
      <div style={{
        background:    `linear-gradient(90deg, ${USYS_NAVY} 0%, #122050 100%)`,
        borderLeft:    `4px solid ${isLive ? '#EF4444' : USYS_RED}`,
        padding:       '10px 16px',
        display:       'flex',
        alignItems:    'center',
        justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* USYS mini shield — white bg so navy is visible */}
          <div style={{ width: 22, height: 22, borderRadius: 4, background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden', padding: 2 }}>
            <img src={mediaUrl(USYS_SHIELD)} alt="USYS"
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
          </div>
          <span style={{ fontSize: 11, fontWeight: 800, color: USYS_WHITE, letterSpacing: '0.8px', textTransform: 'uppercase' as const }}>
            {game.label}
          </span>
          {!loading && isLive && (
            <span style={{ fontSize: 9, fontWeight: 800, color: '#EF4444', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.4)', borderRadius: 20, padding: '2px 7px', letterSpacing: '0.8px' }}>
              🔴 LIVE
            </span>
          )}
          {!loading && isCompleted && (
            <span style={{ fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.4)', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 20, padding: '2px 7px', letterSpacing: '0.5px' }}>
              FINAL{scoreDisplay ? ` · ${scoreDisplay}` : ''}
            </span>
          )}
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: USYS_WHITE, opacity: 0.9 }}>{game.day}</div>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', fontWeight: 600 }}>{game.date}</div>
        </div>
      </div>

      {/* Matchup */}
      <div style={{ padding: '18px 16px 14px', display: 'flex', alignItems: 'center' }}>
        {/* Carmel FC */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 7 }}>
          <TeamLogo src={CARMEL_LOGO} alt="Carmel FC" size={58} initials={carmelInitials}
            bgColor={`${USYS_NAVY}55`} borderColor={`${USYS_NAVY}aa`} />
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: USYS_WHITE, lineHeight: 1.2 }}>Carmel FC</div>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', fontWeight: 600, marginTop: 1 }}>2013G Gold NL</div>
          </div>
        </div>

        {/* VS / score */}
        <div style={{ flexShrink: 0, width: 54, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5 }}>
          {isCompleted && scoreDisplay
            ? <div style={{ fontSize: 19, fontWeight: 900, color: USYS_WHITE, letterSpacing: '-1px' }}>{scoreDisplay}</div>
            : <>
                <div style={{ fontSize: 11, fontWeight: 900, color: USYS_RED, letterSpacing: '1.5px' }}>VS</div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.28)', fontWeight: 600, textAlign: 'center', lineHeight: 1.4 }}>
                  {game.time.replace(' / ', '\n')}
                </div>
              </>
          }
        </div>

        {/* Opponent */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 7 }}>
          <TeamLogo src={game.opponentLogo} alt={game.opponent} size={58} initials={oppInitials} />
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: USYS_WHITE, lineHeight: 1.2 }}>{game.opponent}</div>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', fontWeight: 600, marginTop: 1 }}>{game.opponentSub}</div>
          </div>
        </div>
      </div>

      {/* Time (when upcoming) */}
      {!isCompleted && (
        <div style={{ textAlign: 'center', padding: '0 16px 4px' }}>
          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.32)', fontWeight: 600 }}>⏰ {game.time}</span>
        </div>
      )}

      {/* CTA */}
      <div style={{ padding: '12px 16px 16px', borderTop: '1px solid rgba(255,255,255,0.06)', marginTop: 6, display: 'flex', gap: 10 }}>
        {!isCompleted && (
          <a href={`/live/${game.id}`} style={{
            flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            padding: '11px 20px', borderRadius: 8, textDecoration: 'none',
            background: isLive ? '#EF4444' : USYS_NAVY,
            border: isLive ? 'none' : `1px solid ${USYS_NAVY}`,
            color: USYS_WHITE, fontWeight: 800, fontSize: 14, letterSpacing: '-0.2px',
            whiteSpace: 'nowrap' as const,
            boxShadow: isLive ? '0 2px 12px rgba(239,68,68,0.4)' : 'none',
          }}>
            {isLive
              ? <><div style={{ width: 7, height: 7, borderRadius: '50%', background: 'rgba(255,255,255,0.9)', animation: 'pulse 1.5s infinite' }} /> Watch Live</>
              : <><svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polygon points="3,2 12,7 3,12"/></svg> Watch Stream</>
            }
          </a>
        )}
        {isCompleted && hasRecording && (
          <button onClick={() => setShowPlayer(p => !p)} style={{
            flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            padding: '11px 20px', borderRadius: 8, border: 'none', cursor: 'pointer', touchAction: 'manipulation',
            background: showPlayer ? `${USYS_NAVY}88` : USYS_NAVY,
            color: USYS_WHITE, fontWeight: 800, fontSize: 14, letterSpacing: '-0.2px', whiteSpace: 'nowrap' as const,
          }}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="white"><path d="M3 2.5l9 4.5-9 4.5V2.5z"/></svg>
            {showPlayer ? 'Hide Replay' : 'Watch Replay'}
          </button>
        )}
        {isCompleted && !hasRecording && (
          <span style={{ flex: 1, fontSize: 13, color: 'rgba(255,255,255,0.3)', fontStyle: 'italic', textAlign: 'center' }}>
            Replay uploading soon…
          </span>
        )}
      </div>

      {/* Inline video */}
      {showPlayer && recordingUrl && (
        <div style={{ padding: '0 14px 14px' }}>
          <video src={recordingUrl} controls playsInline autoPlay
            style={{ width: '100%', borderRadius: 8, background: '#000', display: 'block', maxHeight: '70vw' }}
            onError={e => { const v = e.currentTarget; console.error('Video error:', v.error?.code, recordingUrl); }} />
          <div style={{ marginTop: 8, fontSize: 11, color: 'rgba(255,255,255,0.25)', textAlign: 'center' }}>
            Full game recording · Carmel FC vs. {game.opponent}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Bracket play placeholder card ─────────────────────────────────────────────
const BracketPlaceholder: React.FC = () => (
  <div style={{
    background:   'rgba(255,255,255,0.02)',
    border:       '1px dashed rgba(255,255,255,0.12)',
    borderRadius: 16,
    padding:      '24px 20px',
    display:      'flex',
    flexDirection: 'column',
    alignItems:   'center',
    gap:          10,
    textAlign:    'center',
  }}>
    <div style={{ fontSize: 28 }}>🏆</div>
    <div style={{ fontSize: 14, fontWeight: 800, color: 'rgba(255,255,255,0.5)' }}>Bracket Play</div>
    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.28)', lineHeight: 1.6, maxWidth: 280 }}>
      Knockout bracket begins <strong style={{ color: 'rgba(255,255,255,0.45)' }}>Monday, July 20</strong> for teams that advance from group play. Check back here for live streaming links.
    </div>
  </div>
);

// ── Red stripe decorator (mirrors USYS flag stripes) ─────────────────────────
const RedStripes: React.FC = () => (
  <div style={{ display: 'flex', gap: 4, alignItems: 'center', justifyContent: 'center', opacity: 0.35 }}>
    {[60, 100, 140, 100, 60].map((w, i) => (
      <div key={i} style={{ width: w, height: 3, background: USYS_RED, borderRadius: 2 }} />
    ))}
  </div>
);

// ── Main page ─────────────────────────────────────────────────────────────────
const USYSWatch: React.FC = () => (
  <div style={{
    minHeight: '100vh',
    background: USYS_DARK,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  }}>

    {/* ── Hero header ── */}
    <div style={{
      width: '100%',
      background: `linear-gradient(170deg, #122050 0%, ${USYS_DARK} 70%)`,
      borderBottom: `3px solid ${USYS_RED}`,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '40px 24px 36px',
      boxSizing: 'border-box',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Stars-and-stripes texture overlay */}
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        backgroundImage: 'linear-gradient(rgba(255,255,255,0.015) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.015) 1px, transparent 1px)',
        backgroundSize: '36px 36px',
      }} />

      {/* USYS horizontal wordmark at very top — white pill so navy text is visible */}
      <div style={{
        background: 'rgba(255,255,255,0.95)', borderRadius: 8, padding: '6px 14px',
        marginBottom: 24, position: 'relative', display: 'inline-flex', alignItems: 'center',
        boxShadow: '0 2px 12px rgba(0,0,0,0.3)',
      }}>
        <img src={mediaUrl(USYS_HEADER)} alt="US Youth Soccer"
          style={{ height: 26, objectFit: 'contain' }}
          onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
      </div>

      {/* Large USYS shield — white background so navy border/text is visible */}
      <div style={{ position: 'relative', marginBottom: 20 }}>
        <div style={{
          width: 110, height: 110, borderRadius: 18,
          background: 'rgba(255,255,255,0.97)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 6px 24px rgba(0,0,0,0.5), 0 0 0 3px rgba(206,17,38,0.3)',
          overflow: 'hidden', padding: 6,
        }}>
          <img src={mediaUrl(USYS_SHIELD)} alt="USYS"
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
        </div>
      </div>

      {/* Red stripe row */}
      <RedStripes />

      {/* Title */}
      <div style={{ textAlign: 'center', position: 'relative', marginTop: 18, marginBottom: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: USYS_RED, letterSpacing: '3px', textTransform: 'uppercase' as const, marginBottom: 6 }}>
          US Youth Soccer
        </div>
        <div style={{ fontSize: 27, fontWeight: 900, color: USYS_WHITE, letterSpacing: '-0.8px', lineHeight: 1.05 }}>
          National Championships
        </div>
        <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)', marginTop: 7, fontWeight: 500 }}>
          Murfreesboro, Tennessee · 2026
        </div>
      </div>

      {/* Stars */}
      <div style={{ marginBottom: 18 }}>
        <Stars count={5} />
      </div>

      {/* Age group / division pill */}
      <div style={{
        background: USYS_RED,
        borderRadius: 40, padding: '6px 20px', marginBottom: 18,
        boxShadow: `0 2px 14px ${USYS_RED}55`,
      }}>
        <span style={{ fontSize: 11, fontWeight: 800, color: USYS_WHITE, letterSpacing: '2px', textTransform: 'uppercase' as const }}>
          ⚽ Girls 13U · National Championship
        </span>
      </div>

      {/* Carmel FC identity chip */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
        borderRadius: 50, padding: '8px 18px',
      }}>
        <img src={mediaUrl(CARMEL_LOGO)} alt="Carmel FC"
          style={{ width: 28, height: 28, objectFit: 'contain', borderRadius: 6, background: 'rgba(255,255,255,0.1)' }}
          onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
        <span style={{ fontSize: 13, fontWeight: 700, color: 'rgba(255,255,255,0.75)' }}>
          Carmel FC 2013G Gold NL
        </span>
      </div>
    </div>

    {/* ── Group play games ── */}
    <div style={{ width: '100%', maxWidth: 560, padding: '28px 20px 8px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 2 }}>
        <div style={{ width: 10, height: 10, borderRadius: 2, background: USYS_RED, flexShrink: 0 }} />
        <h2 style={{ margin: 0, fontSize: 11, fontWeight: 800, color: 'rgba(255,255,255,0.35)', letterSpacing: '2px', textTransform: 'uppercase' as const }}>
          Group Play
        </h2>
        <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.07)' }} />
        <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', fontWeight: 600 }}>July 16–18</span>
      </div>
      {GROUP_GAMES.map((g, i) => (
        <GameCard key={g.id} game={g} index={i} />
      ))}
    </div>

    {/* ── Bracket play section ── */}
    <div style={{ width: '100%', maxWidth: 560, padding: '24px 20px 8px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 2 }}>
        <div style={{ width: 10, height: 10, borderRadius: 2, background: 'rgba(255,255,255,0.2)', flexShrink: 0 }} />
        <h2 style={{ margin: 0, fontSize: 11, fontWeight: 800, color: 'rgba(255,255,255,0.25)', letterSpacing: '2px', textTransform: 'uppercase' as const }}>
          Bracket Play
        </h2>
        <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.05)' }} />
        <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.2)', fontWeight: 600 }}>Jul 20+</span>
      </div>
      <BracketPlaceholder />
    </div>

    {/* ── Footer ── */}
    <div style={{ width: '100%', maxWidth: 560, padding: '28px 20px 44px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>
      <div style={{ width: '100%', height: 1, background: 'rgba(255,255,255,0.07)' }} />

      {/* USYS horizontal wordmark — white pill in footer */}
      <div style={{
        background: 'rgba(255,255,255,0.9)', borderRadius: 6, padding: '5px 12px',
        display: 'inline-flex', alignItems: 'center',
      }}>
        <img src={mediaUrl(USYS_HEADER)} alt="US Youth Soccer"
          style={{ height: 22, objectFit: 'contain' }}
          onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
      </div>

      <p style={{ margin: 0, fontSize: 13, color: 'rgba(255,255,255,0.3)', textAlign: 'center' }}>
        Get the full experience — live scores, stats &amp; highlights
      </p>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, justifyContent: 'center', alignItems: 'center' }}>
        <AppStoreBadge />
        <a href="https://www.slowrib.com/register" target="_blank" rel="noopener noreferrer"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 20px',
            background: `${USYS_RED}22`, border: `1px solid ${USYS_RED}66`,
            borderRadius: 8, color: '#ff6680', fontWeight: 700, fontSize: 14,
            textDecoration: 'none', whiteSpace: 'nowrap' as const,
          }}
          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = `${USYS_RED}33`; }}
          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = `${USYS_RED}22`; }}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="8" cy="5" r="3"/><path d="M2 14c0-3.3 2.7-6 6-6s6 2.7 6 6"/>
          </svg>
          Create Free Account
        </a>
      </div>

      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.15)', textAlign: 'center', lineHeight: 1.7 }}>
        Powered by{' '}
        <a href="https://www.slowrib.com" style={{ color: 'rgba(255,255,255,0.28)', textDecoration: 'none', fontWeight: 600 }}>Slow Rib Sports</a>
        {' · '}
        <a href="https://www.usyouthsoccer.org/national-championships/" target="_blank" rel="noopener noreferrer"
          style={{ color: 'rgba(255,255,255,0.18)', textDecoration: 'none' }}>USYS Nationals</a>
        {' · '}
        <a href="https://www.slowrib.com/privacy" style={{ color: 'rgba(255,255,255,0.15)', textDecoration: 'none' }}>Privacy</a>
      </div>
    </div>

    <style>{`
      @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.35; } }
    `}</style>
  </div>
);

export default USYSWatch;
